import axios from "axios";

// Smart backend URL resolution:
// - In the browser, if the current origin differs from the env-baked URL (e.g. when
//   deployed on a custom domain like erasmusswap.com), use the current origin so API
//   calls are same-origin (no CORS, no cookie issues).
// - Otherwise fall back to the env-provided URL (preview / local dev).
const ENV_URL = process.env.REACT_APP_BACKEND_URL || "";
const isBrowser = typeof window !== "undefined";

const BACKEND_URL = (() => {
    if (!isBrowser) return ENV_URL;
    if (!ENV_URL) return window.location.origin;
    try {
        const envHost = new URL(ENV_URL).host;
        if (envHost !== window.location.host) return window.location.origin;
    } catch {
        return window.location.origin;
    }
    return ENV_URL;
})();

export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// 20s timeout so requests fail fast instead of hanging the whole UI on a
// flaky connection — the biggest reason users saw the app "stuck on the
// loading screen" when reopening it after a long time.
export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    timeout: 20000,
});

// Token fallback for environments where cookies don't round-trip
let storedToken = localStorage.getItem("auth_token") || null;

// Hook so the AuthContext can react to involuntary logouts (e.g. the token
// expired while the app was in the background). Registered from AuthContext.
let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

export const setAuthToken = (token) => {
    storedToken = token;
    if (token) {
        localStorage.setItem("auth_token", token);
    } else {
        localStorage.removeItem("auth_token");
    }
};

export const getAuthToken = () => storedToken;

api.interceptors.request.use((config) => {
    if (storedToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
});

// Centralised 401 handling: drop the stale token and let AuthContext show
// the login screen immediately, instead of leaving the UI in a half-loaded
// state after a long-expired JWT.
api.interceptors.response.use(
    (res) => res,
    (err) => {
        const status = err.response?.status;
        const url = err.config?.url || "";
        // The /auth/me probe returns 401 for anonymous visitors too, so we
        // only treat it as "signed out" when a token is actually present.
        if (status === 401 && storedToken && !url.includes("/auth/login")) {
            setAuthToken(null);
            if (onUnauthorized) {
                try { onUnauthorized(); } catch { /* ignore */ }
            }
        }
        return Promise.reject(err);
    },
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Er ging iets mis. Probeer opnieuw.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function fileUrl(path) {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${API}/files/${path}`;
}
